import { useState, type FormEvent } from 'react';
import {
  useContractors,
  useCreateContractor,
  useUpdateContractor,
  useDeleteContractor,
  useUploadContractorW9,
} from '../api/queries/contractors';
import { useAssignableUsers } from '../api/queries/project-tasks';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { ApiError, downloadBlob } from '../api/client';
import { can } from '../auth/store';
import { PhoneListInput, type PhoneEntry } from '../components/PhoneListInput';
import {
  formatPhoneDisplay,
  stripPhoneForStorage,
  formatPhoneAsTyped,
} from '../utils/phone';
import type { ContractorTaxIdType } from '@stfors/shared-types';
import { IconAction } from '../components/IconAction';
import { SortableTh } from '../components/SortableTh';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import './ContractorsPage.css';
import { Toggle } from '../components/Toggle';

type ContractorSortKey = 'name' | 'email' | 'phone' | 'tax_id' | 'w9' | 'user';

function maskTaxId(taxId: string | null, type: ContractorTaxIdType | null): string {
  if (!taxId) return '—';
  if (type === 'ssn' && taxId.length >= 4) return `***-**-${taxId.slice(-4)}`;
  if (type === 'ein' && taxId.length >= 4) return `**-***${taxId.slice(-4)}`;
  return '••••';
}

interface DeactivateRowProps {
  id: number;
  legalName: string;
  isActive: boolean;
}

function W9Cell({ id, hasW9, legalName }: { id: number; hasW9: boolean; legalName: string }): JSX.Element {
  const upload = useUploadContractorW9();
  const [error, setError] = useState<string | null>(null);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setError('W-9 must be under 10 MB.');
      return;
    }
    setError(null);
    upload.mutate(
      { contractor_id: id, file: f },
      { onError: () => setError('Upload failed.') },
    );
  };

  const onDownload = () => {
    setError(null);
    downloadBlob(`/api/contractors/${id}/w9`, `${legalName}-W9`).catch(() => {
      setError('Download failed.');
    });
  };

  return (
    <div className="ct-w9">
      {hasW9 ? (
        <button type="button" className="ct-link ct-link-cyan" onClick={onDownload}>
          download
        </button>
      ) : (
        <span className="ct-unlinked">—</span>
      )}
      {can('contractor.write') && (
        <label className="ct-link" style={{ cursor: 'pointer', marginLeft: 8 }}>
          {upload.isPending ? '…' : (hasW9 ? 'replace' : 'upload')}
          <input
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            onChange={onPick}
            disabled={upload.isPending}
          />
        </label>
      )}
      {error && <span className="field-error" style={{ display: 'block' }}>{error}</span>}
    </div>
  );
}

function DeactivateButton({ id, legalName, isActive }: DeactivateRowProps): JSX.Element {
  const update = useUpdateContractor(id);
  return (
    <IconAction
      action={isActive ? 'pause' : 'resume'}
      label={isActive ? 'Deactivate' : 'Reactivate'}
      onClick={() => {
        const verb = isActive ? 'Deactivate' : 'Reactivate';
        if (confirm(`${verb} ${legalName}?`)) {
          update.mutate({ is_active: !isActive });
        }
      }}
      disabled={update.isPending}
      iconSize={16}
    />
  );
}

export function ContractorsPage(): JSX.Element {
  const [showInactive, setShowInactive] = useState(false);

  const { data, isPending, error } = useContractors(
    showInactive ? { active: false } : {},
  );
  const usersQuery = useAssignableUsers();
  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const phoneTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'phone_type');
  const createContractor = useCreateContractor();
  const deleteContractor = useDeleteContractor();

  // Inline-edit state — supports editing the name parts, business name,
  // address, and the full phones list. Email + tax ID + W-9 stay
  // non-inline; admins use a follow-up flow for those (out of scope).
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast] = useState('');
  const [editSuffix, setEditSuffix] = useState('');
  const [editBusiness, setEditBusiness] = useState('');
  const [editPhones, setEditPhones] = useState<PhoneEntry[]>([]);
  const [editAddress, setEditAddress] = useState('');

  const startContractorEdit = (c: {
    id: number;
    title: string | null;
    first_name: string | null;
    last_name: string | null;
    suffix: string | null;
    business_name: string | null;
    phones: { phone_type: string; phone: string }[];
    address: string | null;
  }) => {
    setEditingId(c.id);
    setEditTitle(c.title ?? '');
    setEditFirst(c.first_name ?? '');
    setEditLast(c.last_name ?? '');
    setEditSuffix(c.suffix ?? '');
    setEditBusiness(c.business_name ?? '');
    setEditPhones(
      c.phones.map((p) => ({
        phone_type: p.phone_type,
        phone: formatPhoneAsTyped(p.phone),
      })),
    );
    setEditAddress(c.address ?? '');
  };

  // Create form state.
  const [showNew, setShowNew] = useState(false);
  const [kind, setKind] = useState<'individual' | 'company'>('individual');
  const [companyName, setCompanyName] = useState('');
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phones, setPhones] = useState<PhoneEntry[]>([{ phone_type: 'Mobile', phone: '' }]);
  const [taxIdType, setTaxIdType] = useState<ContractorTaxIdType | ''>('');
  const [taxId, setTaxId] = useState('');
  const [address, setAddress] = useState('');
  const [linkedUser, setLinkedUser] = useState<number | ''>('');
  const [createError, setCreateError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (email.trim().length === 0) return;
    if (kind === 'individual' && lastName.trim().length === 0) return;
    if (kind === 'company' && companyName.trim().length === 0) return;

    const idGiven = taxId.trim().length > 0;
    const typeGiven = taxIdType !== '';
    if (idGiven !== typeGiven) {
      setCreateError('Tax ID and tax ID type must both be provided, or both omitted.');
      return;
    }

    const phonesPayload = phones
      .map((p) => ({
        phone_type: p.phone_type,
        phone: stripPhoneForStorage(p.phone) ?? '',
      }))
      .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);

    try {
      await createContractor.mutateAsync({
        kind,
        title: kind === 'individual' ? title.trim() || undefined : undefined,
        first_name: kind === 'individual' ? firstName.trim() || undefined : undefined,
        last_name: kind === 'individual' ? lastName.trim() : undefined,
        suffix: kind === 'individual' ? suffix.trim() || undefined : undefined,
        company_name: kind === 'company' ? companyName.trim() || undefined : undefined,
        business_name: kind === 'individual' ? businessName.trim() || undefined : undefined,
        email: email.trim(),
        phones: kind === 'individual' && phonesPayload.length > 0 ? phonesPayload : undefined,
        tax_id: idGiven ? taxId.trim() : undefined,
        tax_id_type: typeGiven ? (taxIdType as ContractorTaxIdType) : undefined,
        address: address.trim() || undefined,
        user_id: linkedUser === '' ? undefined : linkedUser,
      });
      setShowNew(false);
      setKind('individual');
      setCompanyName('');
      setTitle('');
      setFirstName('');
      setLastName('');
      setSuffix('');
      setBusinessName('');
      setEmail('');
      setPhones([{ phone_type: 'Mobile', phone: '' }]);
      setTaxIdType('');
      setTaxId('');
      setAddress('');
      setLinkedUser('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { fieldErrors?: Record<string, string[]> } } | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const first = Object.values(fe).find((arr) => Array.isArray(arr) && arr.length > 0);
          if (first && Array.isArray(first)) {
            setCreateError(first[0]!);
            return;
          }
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setCreateError("You don't have permission to add contractors.");
        return;
      }
      setCreateError('Could not save. Try again.');
    }
  };

  const contractors = data?.contractors ?? [];
  const users = usersQuery.data?.users ?? [];
  const sort = useClientSort(contractors, (c, key: ContractorSortKey) => {
    switch (key) {
      case 'name': return c.display_name;
      case 'email': return c.email;
      case 'phone': {
        const primary = c.phones.find((p) => p.is_primary) ?? c.phones[0];
        return primary?.phone ?? '';
      }
      case 'tax_id': return c.tax_id ?? '';
      case 'w9': return c.w9_path ? 1 : 0;
      case 'user': return c.user_display_name ?? '';
      default: return '';
    }
  }, { key: 'name', dir: 'asc' });

  if (isPending) return <p className="ct-status">Loading contractors…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view contractors."
        : 'Failed to load contractors.';
    return <p className="ct-status ct-error">{msg}</p>;
  }

  return (
    <section className="page-stack page-stack-wide">
      <div className="card ct-toolbar">
        <header className="ct-header">
          <h2>Contractors</h2>
          <div className="ct-header-controls">
            <Toggle
              checked={showInactive}
              onChange={setShowInactive}
              label="Show inactive only"
            />
            {can('contractor.write') && !showNew && (
              <button
                type="button"
                className="btn-cyan-primary"
                onClick={() => setShowNew(true)}
              >
                New contractor
              </button>
            )}
          </div>
        </header>
      </div>

      {showNew && (
        <div className="card">
        <form className="ct-new-form" onSubmit={onCreate}>
          <div className="ct-new-row">
            <button
              type="button"
              className={kind === 'individual' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
              onClick={() => setKind('individual')}
              disabled={createContractor.isPending}
            >
              Individual
            </button>
            <button
              type="button"
              className={kind === 'company' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
              onClick={() => setKind('company')}
              disabled={createContractor.isPending}
            >
              Company
            </button>
          </div>

          {kind === 'company' && (
            <div className="ct-new-row">
              <label className="ct-field ct-field-wide">
                <span>Company name *</span>
                <input
                  type="text"
                  className="form-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  maxLength={200}
                  autoComplete="organization"
                  disabled={createContractor.isPending}
                />
              </label>
            </div>
          )}

          {kind === 'individual' && (
          <div className="ct-new-row ct-new-row-name">
            <label className="ct-field ct-field-narrow">
              <span>Title</span>
              <select
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoComplete="honorific-prefix"
                disabled={createContractor.isPending}
              >
                <option value="">—</option>
                {titleOptions.map((o) => (
                  <option key={o.id} value={o.value}>{o.value}</option>
                ))}
              </select>
            </label>
            <label className="ct-field">
              <span>First name</span>
              <input
                type="text"
                className="form-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={100}
                autoComplete="given-name"
                disabled={createContractor.isPending}
              />
            </label>
            <label className="ct-field">
              <span>Last name *</span>
              <input
                type="text"
                className="form-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={100}
                required
                autoComplete="family-name"
                disabled={createContractor.isPending}
              />
            </label>
            <label className="ct-field ct-field-narrow">
              <span>Suffix</span>
              <select
                className="form-input"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                autoComplete="honorific-suffix"
                disabled={createContractor.isPending}
              >
                <option value="">—</option>
                {suffixOptions.map((o) => (
                  <option key={o.id} value={o.value}>{o.value}</option>
                ))}
              </select>
            </label>
            <label className="ct-field">
              <span>Business name</span>
              <input
                type="text"
                className="form-input"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                maxLength={200}
                disabled={createContractor.isPending}
              />
            </label>
          </div>
          )}
          <div className="ct-new-row">
            <label className="ct-field ct-field-wide">
              <span>Email *</span>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={254}
                required
                disabled={createContractor.isPending}
              />
            </label>
          </div>
          {kind === 'individual' && (
          <div className="ct-field ct-field-wide">
            <span>Phone numbers</span>
            <PhoneListInput
              value={phones}
              onChange={setPhones}
              typeOptions={phoneTypeOptions}
              disabled={createContractor.isPending}
            />
          </div>
          )}
          <div className="ct-new-row">
            <label className="ct-field">
              <span>Tax ID type</span>
              <select
                className="form-input"
                value={taxIdType}
                onChange={(e) => setTaxIdType(e.target.value as ContractorTaxIdType | '')}
                disabled={createContractor.isPending}
              >
                <option value="">—</option>
                <option value="ein">EIN</option>
                <option value="ssn">SSN</option>
              </select>
            </label>
            <label className="ct-field">
              <span>Tax ID</span>
              <input
                type="text"
                className="form-input"
                value={taxId}
                onChange={(e) => setTaxId(e.target.value)}
                maxLength={64}
                placeholder={taxIdType === 'ssn' ? '123-45-6789' : taxIdType === 'ein' ? '12-3456789' : ''}
                disabled={createContractor.isPending}
              />
            </label>
            <label className="ct-field">
              <span>Linked user</span>
              <select
                className="form-input"
                value={linkedUser}
                onChange={(e) => {
                  const v = e.target.value;
                  setLinkedUser(v === '' ? '' : Number(v));
                }}
                disabled={createContractor.isPending}
              >
                <option value="">(no system access)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="ct-field ct-field-wide">
            <span>Address</span>
            <textarea
              className="form-input ct-textarea"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
              maxLength={500}
              disabled={createContractor.isPending}
            />
          </label>
          {createError && <p className="ct-error-banner" role="alert">{createError}</p>}
          <div className="ct-new-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowNew(false);
                setCreateError(null);
              }}
              disabled={createContractor.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={
                createContractor.isPending ||
                email.trim().length === 0 ||
                (kind === 'individual' ? lastName.trim().length === 0 : companyName.trim().length === 0)
              }
            >
              {createContractor.isPending ? 'Saving…' : 'Save contractor'}
            </button>
          </div>
        </form>
        </div>
      )}

      {contractors.length === 0 ? (
        <div className="card">
          <div className="ct-empty">
            <p>No {showInactive ? 'inactive' : 'active'} contractors.</p>
            {can('contractor.write') && !showInactive && (
              <p className="ct-empty-meta">Click "New contractor" to onboard one.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="card card-tight ct-table-card table-scroll-card">
        <TableScroll id="contractors">
        <table className="ct-table">
          <thead>
            <tr>
              <SortableTh column="name" label="Name" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="email" label="Email" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="phone" label="Phone" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="tax_id" label="Tax ID" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="w9" label="W-9" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              <SortableTh column="user" label="System user" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />
              {(can('contractor.write') || can('contractor.delete')) && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((c) => {
              const isEditing = editingId === c.id;
              const primaryPhone = c.phones.find((p) => p.is_primary) ?? c.phones[0];
              return (
              <tr key={c.id} className={c.is_active ? '' : 'ct-row-inactive'}>
                <td>
                  {isEditing ? (
                    <>
                      <select
                        className="form-input ct-edit-input"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        aria-label="Title"
                      >
                        <option value="">— Title —</option>
                        {titleOptions.map((o) => (
                          <option key={o.id} value={o.value}>{o.value}</option>
                        ))}
                        {editTitle && !titleOptions.some((o) => o.value === editTitle) && (
                          <option value={editTitle}>{editTitle} (legacy)</option>
                        )}
                      </select>
                      <input
                        type="text"
                        className="form-input ct-edit-input"
                        value={editFirst}
                        onChange={(e) => setEditFirst(e.target.value)}
                        placeholder="First name"
                        style={{ marginTop: 4 }}
                      />
                      <input
                        type="text"
                        className="form-input ct-edit-input"
                        value={editLast}
                        onChange={(e) => setEditLast(e.target.value)}
                        placeholder="Last name"
                        style={{ marginTop: 4 }}
                      />
                      <select
                        className="form-input ct-edit-input"
                        value={editSuffix}
                        onChange={(e) => setEditSuffix(e.target.value)}
                        aria-label="Suffix"
                        style={{ marginTop: 4 }}
                      >
                        <option value="">— Suffix —</option>
                        {suffixOptions.map((o) => (
                          <option key={o.id} value={o.value}>{o.value}</option>
                        ))}
                        {editSuffix && !suffixOptions.some((o) => o.value === editSuffix) && (
                          <option value={editSuffix}>{editSuffix} (legacy)</option>
                        )}
                      </select>
                      <input
                        type="text"
                        className="form-input ct-edit-input"
                        value={editBusiness}
                        onChange={(e) => setEditBusiness(e.target.value)}
                        placeholder="Business name"
                        style={{ marginTop: 4 }}
                      />
                    </>
                  ) : (
                    <>
                      <div className="ct-name">{c.display_name}</div>
                      {c.business_name && (
                        <div className="ct-business">{c.business_name}</div>
                      )}
                    </>
                  )}
                </td>
                <td>
                  <a
                    href={`mailto:${c.email}`}
                    className="ct-mail-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {c.email}
                  </a>
                </td>
                <td>
                  {isEditing ? (
                    <PhoneListInput
                      value={editPhones}
                      onChange={setEditPhones}
                      typeOptions={phoneTypeOptions}
                    />
                  ) : c.phones.length === 0 ? (
                    '—'
                  ) : (
                    <>
                      {primaryPhone && (
                        <div>
                          <span className="ct-phone-type">{primaryPhone.phone_type}</span>{' '}
                          {formatPhoneDisplay(primaryPhone.phone)}
                        </div>
                      )}
                      {c.phones.length > 1 && (
                        <div className="ct-phone-more">+{c.phones.length - 1} more</div>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {c.tax_id ? (
                    <span title={`${c.tax_id_type?.toUpperCase()} on file`}>
                      <code>{maskTaxId(c.tax_id, c.tax_id_type)}</code>
                      <span className="ct-tax-type"> {c.tax_id_type?.toUpperCase()}</span>
                    </span>
                  ) : '—'}
                </td>
                <td>
                  <W9Cell id={c.id} hasW9={!!c.w9_path} legalName={c.display_name} />
                </td>
                <td>
                  {c.user_display_name ? (
                    <span className="ct-chip ct-chip-cyan">{c.user_display_name}</span>
                  ) : (
                    <span className="ct-unlinked">—</span>
                  )}
                </td>
                {(can('contractor.write') || can('contractor.delete')) && (
                  <td className="ct-actions">
                    {isEditing ? (
                      <SaveContractorEdit
                        id={c.id}
                        title={editTitle}
                        first_name={editFirst}
                        last_name={editLast}
                        suffix={editSuffix}
                        business_name={editBusiness}
                        phones={editPhones}
                        address={editAddress}
                        onDone={() => setEditingId(null)}
                      />
                    ) : (
                      <>
                        {can('contractor.write') && (
                          <IconAction
                            action="edit"
                            onClick={() => startContractorEdit(c)}
                            iconSize={16}
                          />
                        )}
                        {can('contractor.write') && (
                          <DeactivateButton
                            id={c.id}
                            legalName={c.display_name}
                            isActive={c.is_active}
                          />
                        )}
                        {can('contractor.delete') && (
                          <IconAction
                            action="delete"
                            onClick={() => {
                              if (confirm(`Delete ${c.display_name}?\n\nThis is a soft delete; row stays in the audit log.`)) {
                                deleteContractor.mutate(c.id);
                              }
                            }}
                            disabled={deleteContractor.isPending}
                            iconSize={16}
                          />
                        )}
                      </>
                    )}
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
              </TableScroll>
        </div>
      )}
    </section>
  );
}

interface SaveEditProps {
  id: number;
  title: string;
  first_name: string;
  last_name: string;
  suffix: string;
  business_name: string;
  phones: PhoneEntry[];
  address: string;
  onDone: () => void;
}

function SaveContractorEdit(props: SaveEditProps): JSX.Element {
  const update = useUpdateContractor(props.id);
  return (
    <>
      <IconAction
        action="save"
        onClick={() => {
          const phonesPayload = props.phones
            .map((p) => ({
              phone_type: p.phone_type,
              phone: stripPhoneForStorage(p.phone) ?? '',
            }))
            .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);
          update.mutate(
            {
              title: props.title.trim() || null,
              first_name: props.first_name.trim() || null,
              last_name: props.last_name.trim(),
              suffix: props.suffix.trim() || null,
              business_name: props.business_name.trim() || null,
              phones: phonesPayload,
              address: props.address.trim() || null,
            },
            { onSuccess: props.onDone },
          );
        }}
        disabled={update.isPending || props.last_name.trim().length === 0}
        iconSize={16}
      />
      <IconAction
        action="cancel"
        onClick={props.onDone}
        disabled={update.isPending}
        iconSize={16}
      />
    </>
  );
}
